import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { createClient } from '@supabase/supabase-js';
import { useSEO, generateContactPageSchema, generateWebPageSchema } from '../../utils/seo';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Contact() {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    codePostal: '',
    date: '',
    time: '',
    pack: '',
    supplements: [] as string[],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'PRT Proprete - Contact',
    description: 'Vous souhaitez offrir à votre véhicule un lavage intérieur et extérieur de qualité professionnelle ? Avec nos Pack Premium (60 €) et Pack Luxe (80 €), bénéficiez d\'un service premium et soigné, directement à votre porte grâce à notre déplacement gratuit dans un rayon de 35 km autour de Creil.\nContactez-nous dès maintenant pour réserver votre prestation ou obtenir plus d\'informations !',
    keywords: 'lavage auto Creil, préparateur auto, nettoyage voiture, detailing auto, lavage intérieur, lavage extérieur, Creil 60',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateContactPageSchema(),
        generateWebPageSchema('Contact - PRT Propreté', 'Réservez votre prestation de lavage automobile')
      ]
    }
  });

  // Créneaux horaires de 9h à 18h (prestations de 3h)
  const allTimeSlots = [
    '09:00', '12:00', '15:00'
  ];

  // Charger les créneaux disponibles quand une date est sélectionnée
  useEffect(() => {
    if (formData.date) {
      loadAvailableSlots(formData.date);
    } else {
      setAvailableSlots([]);
    }
  }, [formData.date]);

  const loadAvailableSlots = async (selectedDate: string) => {
    setLoadingSlots(true);
    try {
      console.log('🔍 Chargement des créneaux pour:', selectedDate);
      
      // Récupérer toutes les réservations pour cette date
      const { data: appointments, error } = await supabase
        .from('appointments')
        .select('time')
        .eq('date', selectedDate);

      console.log('📅 Réservations trouvées:', appointments);
      console.log('❌ Erreur éventuelle:', error);

      if (error) {
        console.error('Erreur Supabase:', error);
        // En cas d'erreur, afficher tous les créneaux
        setAvailableSlots(allTimeSlots);
        setLoadingSlots(false);
        return;
      }

      // Si aucune réservation, tous les créneaux sont disponibles
      if (!appointments || appointments.length === 0) {
        console.log('✨ Aucune réservation - tous les créneaux sont disponibles');
        setAvailableSlots(allTimeSlots);
        setLoadingSlots(false);
        return;
      }

      // Extraire les heures déjà réservées
      const bookedTimes = appointments.map(apt => apt.time);
      console.log('⏰ Créneaux réservés:', bookedTimes);
      
      // Filtrer les créneaux disponibles
      const available = allTimeSlots.filter(slot => !bookedTimes.includes(slot));
      console.log('✅ Créneaux disponibles:', available);
      
      setAvailableSlots(available);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des créneaux:', error);
      // En cas d'erreur, afficher tous les créneaux
      setAvailableSlots(allTimeSlots);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Si on change la date, réinitialiser l'heure
    if (name === 'date') {
      setFormData(prev => ({ ...prev, [name]: value, time: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSupplementChange = (supplement: string) => {
    setFormData(prev => ({
      ...prev,
      supplements: prev.supplements.includes(supplement)
        ? prev.supplements.filter(s => s !== supplement)
        : [...prev.supplements, supplement]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validation
    if (!formData.nom || !formData.prenom || !formData.email || !formData.telephone || !formData.adresse || !formData.ville || !formData.codePostal || !formData.pack || !formData.date || !formData.time) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      // Vérifier une dernière fois que le créneau est toujours disponible
      const { data: existingAppointment } = await supabase
        .from('appointments')
        .select('id')
        .eq('date', formData.date)
        .eq('time', formData.time)
        .maybeSingle();

      if (existingAppointment) {
        alert('Désolé, ce créneau vient d\'être réservé. Veuillez en choisir un autre.');
        loadAvailableSlots(formData.date);
        setIsSubmitting(false);
        return;
      }

      // Insérer la réservation dans Supabase
      const { error: insertError } = await supabase
        .from('appointments')
        .insert([
          {
            nom: formData.nom,
            prenom: formData.prenom,
            email: formData.email,
            telephone: formData.telephone,
            adresse: formData.adresse,
            ville: formData.ville,
            code_postal: formData.codePostal,
            date: formData.date,
            time: formData.time,
            pack: formData.pack,
            supplements: formData.supplements,
            message: formData.message,
            motif: `Réservation ${formData.pack}`,
            status: 'pending'
          }
        ]);

      if (insertError) throw insertError;

      // Envoyer l'email de notification à PRT PROPRETER
      await fetch('https://toyrhgqqqykglzvprvkc.supabase.co/functions/v1/prt-notify-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone,
          adresse: formData.adresse,
          ville: formData.ville,
          codePostal: formData.codePostal,
          date: formData.date,
          time: formData.time,
          pack: formData.pack,
          supplements: formData.supplements,
          message: formData.message
        })
      });

      // Envoyer l'email de confirmation au client
      await fetch('https://toyrhgqqqykglzvprvkc.supabase.co/functions/v1/prt-confirm-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone,
          adresse: formData.adresse,
          ville: formData.ville,
          codePostal: formData.codePostal,
          date: formData.date,
          time: formData.time,
          pack: formData.pack,
          supplements: formData.supplements,
          message: formData.message
        })
      });

      // Envoyer aussi au formulaire Readdy
      const formBody = new URLSearchParams();
      formBody.append('nom', formData.nom);
      formBody.append('prenom', formData.prenom);
      formBody.append('email', formData.email);
      formBody.append('telephone', formData.telephone);
      formBody.append('adresse', formData.adresse);
      formBody.append('ville', formData.ville);
      formBody.append('code_postal', formData.codePostal);
      formBody.append('date', formData.date);
      formBody.append('heure', formData.time);
      formBody.append('pack', formData.pack);
      formBody.append('supplements', formData.supplements.join(', '));
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d60o9h8omoleh5thv9ig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      setSubmitStatus('success');
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        ville: '',
        codePostal: '',
        date: '',
        time: '',
        pack: '',
        supplements: [],
        message: ''
      });
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const supplements = [
    { id: 'poils', label: 'Poils d\'animaux', price: '20€' },
    { id: 'sieges-enfants', label: 'Sièges enfants', price: '15€' },
    { id: 'tres-sale', label: 'Véhicule très sale', price: '20€' }
  ];

  // Date minimum = aujourd'hui
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a3e] via-[#2d2d6e] to-[#4a4aaa]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 md:pt-32 pb-10 sm:pb-12 md:pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">
            Réserver Maintenant
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-2">
            Réservez votre prestation dès maintenant
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-10 sm:py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
            {/* Contact Form */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-10 order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Réserver une Prestation</h2>
              
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="nom" className="block text-white font-semibold mb-2 text-sm sm:text-base">
                      Nom <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors duration-200 text-base"
                      placeholder="Votre nom"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="prenom" className="block text-white font-semibold mb-2 text-sm sm:text-base">
                      Prénom <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="prenom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors duration-200 text-base"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-white font-semibold mb-2 text-sm sm:text-base">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors duration-200 text-base"
                    placeholder="votre.email@exemple.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="telephone" className="block text-white font-semibold mb-2 text-sm sm:text-base">
                    Téléphone <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors duration-200 text-base"
                    placeholder="06 12 34 56 78"
                  />
                </div>

                <div>
                  <label htmlFor="adresse" className="block text-white font-semibold mb-2 text-sm sm:text-base">
                    Adresse de la prestation <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="adresse"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors duration-200 text-base"
                    placeholder="Numéro et nom de rue"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="codePostal" className="block text-white font-semibold mb-2 text-sm sm:text-base">
                      Code postal <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="codePostal"
                      name="codePostal"
                      value={formData.codePostal}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{5}"
                      maxLength={5}
                      className="w-full px-4 py-3 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors duration-200 text-base"
                      placeholder="60100"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="ville" className="block text-white font-semibold mb-2 text-sm sm:text-base">
                      Ville <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="ville"
                      name="ville"
                      value={formData.ville}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors duration-200 text-base"
                      placeholder="Creil"
                    />
                  </div>
                </div>

                <p className="text-cyan-400 text-xs sm:text-sm -mt-2">
                  <i className="ri-car-line mr-1"></i>
                  Nous nous déplaçons gratuitement dans un rayon de 35km autour de Creil (60)
                </p>

                {/* Date Selection */}
                <div>
                  <label htmlFor="date" className="block text-white font-semibold mb-2 text-sm sm:text-base">
                    Date de la prestation <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    min={today}
                    required
                    className="w-full px-4 py-3 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors duration-200 text-base"
                  />
                </div>

                {/* Time Slot Selection */}
                {formData.date && (
                  <div>
                    <label className="block text-white font-semibold mb-3 text-sm sm:text-base">
                      Heure de la prestation <span className="text-red-400">*</span>
                    </label>
                    {loadingSlots ? (
                      <div className="text-center py-8">
                        <i className="ri-loader-4-line animate-spin text-cyan-400 text-3xl"></i>
                        <p className="text-gray-300 mt-2">Chargement des créneaux disponibles...</p>
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-center">
                        <i className="ri-calendar-close-line text-red-400 text-3xl mb-2"></i>
                        <p className="text-red-400 font-semibold">Aucun créneau disponible pour cette date</p>
                        <p className="text-gray-300 text-sm mt-1">Veuillez choisir une autre date</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {availableSlots.map((slot) => (
                          <label
                            key={slot}
                            className={`cursor-pointer rounded-xl p-4 text-center transition-all duration-300 ${
                              formData.time === slot
                                ? 'bg-cyan-400 text-[#1a1a3e] border-2 border-cyan-400 shadow-lg'
                                : 'bg-white/10 text-white border-2 border-white/20 hover:bg-white/20'
                            }`}
                          >
                            <input
                              type="radio"
                              name="time"
                              value={slot}
                              checked={formData.time === slot}
                              onChange={handleChange}
                              className="sr-only"
                            />
                            <div className="flex items-center justify-center">
                              <i className="ri-time-line mr-2"></i>
                              <span className="font-bold text-lg">{slot}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    <p className="text-gray-400 text-xs sm:text-sm mt-3">
                      <i className="ri-information-line mr-1"></i>
                      Chaque prestation dure environ 3 heures
                    </p>
                  </div>
                )}
                
                {/* Pack Selection */}
                <div>
                  <label className="block text-white font-semibold mb-3 text-sm sm:text-base">
                    Choisissez votre Pack <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Pack Luxe - Mis en avant */}
                    <label 
                      className={`relative cursor-pointer rounded-xl sm:rounded-2xl p-4 sm:p-5 transition-all duration-300 ${
                        formData.pack === 'Pack Luxe - 80€' 
                          ? 'bg-cyan-400/30 border-2 border-cyan-400' 
                          : 'bg-white/10 border-2 border-cyan-400/50 hover:bg-white/15'
                      }`}
                    >
                      <div className="absolute -top-2 -right-2 bg-cyan-400 text-[#1a1a3e] px-2 py-0.5 rounded-full text-xs font-bold">
                        <i className="ri-star-fill mr-1"></i>
                        Recommandé
                      </div>
                      <input
                        type="radio"
                        name="pack"
                        value="Pack Luxe - 80€"
                        checked={formData.pack === 'Pack Luxe - 80€'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-bold text-base sm:text-lg">Pack Luxe</span>
                        <span className="text-cyan-400 font-bold text-lg sm:text-xl">80€</span>
                      </div>
                      <p className="text-gray-300 text-xs sm:text-sm">Intérieur + Extérieur complet</p>
                      <div className={`mt-2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.pack === 'Pack Luxe - 80€' ? 'border-cyan-400 bg-cyan-400' : 'border-white/50'
                      }`}>
                        {formData.pack === 'Pack Luxe - 80€' && (
                          <i className="ri-check-line text-[#1a1a3e] text-sm"></i>
                        )}
                      </div>
                    </label>
                    
                    {/* Pack Premium */}
                    <label 
                      className={`cursor-pointer rounded-xl sm:rounded-2xl p-4 sm:p-5 transition-all duration-300 ${
                        formData.pack === 'Pack Premium - 60€' 
                          ? 'bg-cyan-400/30 border-2 border-cyan-400' 
                          : 'bg-white/10 border-2 border-white/20 hover:bg-white/15'
                      }`}
                    >
                      <input
                        type="radio"
                        name="pack"
                        value="Pack Premium - 60€"
                        checked={formData.pack === 'Pack Premium - 60€'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-bold text-base sm:text-lg">Pack Premium</span>
                        <span className="text-cyan-400 font-bold text-lg sm:text-xl">60€</span>
                      </div>
                      <p className="text-gray-300 text-xs sm:text-sm">Intérieur complet</p>
                      <div className={`mt-2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.pack === 'Pack Premium - 60€' ? 'border-cyan-400 bg-cyan-400' : 'border-white/50'
                      }`}>
                        {formData.pack === 'Pack Premium - 60€' && (
                          <i className="ri-check-line text-[#1a1a3e] text-sm"></i>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
                
                {/* Suppléments - Choix multiples */}
                <div>
                  <label className="block text-white font-semibold mb-3 text-sm sm:text-base">
                    Suppléments <span className="text-gray-400 font-normal">(optionnel)</span>
                  </label>
                  <div className="space-y-2 sm:space-y-3">
                    {supplements.map((supplement) => (
                      <label 
                        key={supplement.id}
                        className={`flex items-center justify-between cursor-pointer rounded-lg sm:rounded-xl p-3 sm:p-4 transition-all duration-300 ${
                          formData.supplements.includes(supplement.label)
                            ? 'bg-cyan-400/20 border border-cyan-400/50'
                            : 'bg-white/10 border border-white/20 hover:bg-white/15'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 transition-all duration-200 ${
                            formData.supplements.includes(supplement.label)
                              ? 'border-cyan-400 bg-cyan-400'
                              : 'border-white/50'
                          }`}>
                            {formData.supplements.includes(supplement.label) && (
                              <i className="ri-check-line text-[#1a1a3e] text-sm"></i>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.supplements.includes(supplement.label)}
                            onChange={() => handleSupplementChange(supplement.label)}
                            className="sr-only"
                          />
                          <span className="text-white text-sm sm:text-base">{supplement.label}</span>
                        </div>
                        <span className="text-cyan-400 font-bold text-sm sm:text-base">+{supplement.price}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-white font-semibold mb-2 text-sm sm:text-base">
                    Message <span className="text-gray-400 font-normal">(optionnel)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors duration-200 resize-none text-base"
                    placeholder="Décrivez votre besoin, l'état de votre véhicule, ou toute information utile..."
                  ></textarea>
                  <p className="text-gray-400 text-xs sm:text-sm mt-2">Maximum 500 caractères</p>
                </div>
                
                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-3 sm:p-4 flex items-start">
                    <i className="ri-checkbox-circle-line text-green-400 text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0"></i>
                    <div>
                      <p className="text-green-400 font-semibold text-sm sm:text-base">Réservation confirmée !</p>
                      <p className="text-gray-300 text-xs sm:text-sm">Votre rendez-vous a été enregistré. Nous vous enverrons une confirmation par email.</p>
                    </div>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 sm:p-4 flex items-start">
                    <i className="ri-error-warning-line text-red-400 text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0"></i>
                    <div>
                      <p className="text-red-400 font-semibold text-sm sm:text-base">Erreur lors de la réservation</p>
                      <p className="text-gray-300 text-xs sm:text-sm">Veuillez vérifier les champs et réessayer.</p>
                    </div>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.date || !formData.time}
                  className="whitespace-nowrap w-full px-6 sm:px-8 py-4 sm:py-5 bg-cyan-400 text-[#1a1a3e] rounded-full text-base sm:text-lg font-bold hover:bg-cyan-300 transition-all duration-300 shadow-lg hover:shadow-cyan-400/50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Réservation en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-line mr-2"></i>
                      Réserver Maintenant
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-4 sm:space-y-6 md:space-y-8 order-1 lg:order-2">
              {/* Contact Direct */}
              <div className="bg-gradient-to-br from-cyan-400/20 to-blue-500/20 backdrop-blur-md border-2 border-cyan-400/50 rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Contact Direct</h2>
                
                <div className="space-y-4 sm:space-y-6">
                  <a 
                    href="tel:0776969414"
                    className="flex items-start bg-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:bg-white/20 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-cyan-400/30 rounded-full flex items-center justify-center mr-4 sm:mr-5 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      <i className="ri-phone-line text-xl sm:text-2xl text-cyan-400"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-base sm:text-lg mb-1">Téléphone</h3>
                      <p className="text-cyan-400 text-lg sm:text-xl font-semibold">07.76.96.94.14</p>
                      <p className="text-gray-300 text-xs sm:text-sm mt-1">Appelez-nous directement</p>
                    </div>
                  </a>
                  
                  <div className="flex items-start bg-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-cyan-400/30 rounded-full flex items-center justify-center mr-4 sm:mr-5 flex-shrink-0">
                      <i className="ri-map-pin-line text-xl sm:text-2xl text-cyan-400"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-base sm:text-lg mb-1">Zone d'intervention</h3>
                      <p className="text-gray-300 text-sm sm:text-base">Creil (60)</p>
                      <p className="text-cyan-400 text-xs sm:text-sm mt-2 font-semibold">
                        <i className="ri-car-line mr-1"></i>
                        Déplacement gratuit dans un rayon de 35kms
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Horaires */}
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Horaires de Réservation</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center bg-white/10 rounded-xl p-4">
                    <i className="ri-calendar-line text-cyan-400 text-2xl mr-4"></i>
                    <div>
                      <p className="text-white font-bold">Tous les jours</p>
                      <p className="text-cyan-400 text-lg font-semibold">9h00 - 18h00</p>
                    </div>
                  </div>
                  <div className="bg-cyan-400/10 border border-cyan-400/30 rounded-xl p-4">
                    <p className="text-gray-300 text-sm">
                      <i className="ri-time-line text-cyan-400 mr-2"></i>
                      Chaque prestation dure environ 3 heures
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Suivez-Nous</h2>
                
                <div className="space-y-3 sm:space-y-4">
                  <a 
                    href="https://snapchat.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center bg-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-5 hover:bg-white/20 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-400/30 rounded-full flex items-center justify-center mr-3 sm:mr-4 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      <i className="ri-snapchat-line text-cyan-400 text-xl sm:text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm sm:text-base">Snapchat</p>
                      <p className="text-cyan-400 text-sm sm:text-base">PRTPROPRETE</p>
                    </div>
                  </a>
                  
                  <a 
                    href="https://instagram.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center bg-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-5 hover:bg-white/20 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-400/30 rounded-full flex items-center justify-center mr-3 sm:mr-4 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      <i className="ri-instagram-line text-cyan-400 text-xl sm:text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm sm:text-base">Instagram</p>
                      <p className="text-cyan-400 text-sm sm:text-base">prt.proprete</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}